import { inject, injectable } from "@launchtray/tsyringe-async";
import { NotificationLayerProvider } from "by/gamefactory/cityversetycoon/composition/providers/NotificationLayerProvider";
import { NotificationLayer } from "by/gamefactory/scene/layer/NotificationLayer";
import { gsap } from "gsap";
import { Container, DisplayObject, FederatedPointerEvent, IDestroyOptions, Point, utils } from "pixi.js";
import { nameof } from "../../../../../shared/bindable-data";
import { ISettingsController } from "../../../../settings/ISettingsController";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { BrokenShieldAnimation } from "../../../common/components/animations/BrokenShieldAnimation";
import { ShieldPartsTexturePack } from "../../../common/components/animations/texturesPacks/ShieldPartsTexturePack";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { CoinsField } from "./CoinsField";
import { UserProfileResourcePackage } from "../../../popups/userprofile/resources/UserProfileResourcePackage";
import { HBox } from "../../../common/components/ui/HBox";
import { ShieldField } from "./ShieldField";
import { TokensField } from "./TokensField";
import { AvatarField } from "./AvatarField";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { Event } from "event-system";
type Timeline = gsap.core.Timeline;

@injectable()
export class TopPanel extends HBox {
    public static readonly GLOBAL_TOP_PANEL_POSITION = { x: 50, y: utils.isMobile.any ? 0 : 25 };
    public static readonly DEFAULT_POSITION_Y = 143;

    private static readonly CHILD_SPACING = 20;

    public readonly onLayerChanged: Event<void>;

    private readonly commonResources: CommonResourcesPackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly guestModel: IGuestModel;
    private readonly playerModel: IPlayerModel;
    private readonly settingsController: ISettingsController;
    private readonly notificationLayer: NotificationLayer;
    private readonly logger: ILogger;

    private menuButton: ButtonWithScaleAnimation;

    private avatarField: AvatarField;
    private coinsField: CoinsField;
    private tokensField: TokensField;

    private shieldField: ShieldField;
    private shieldsValue: number;

    private originParent: Container;
    private childIndexOnOriginalParent: number;
    private initialPositionYValue: number;
    private animationTimeLine: Timeline;

    private coinsFieldIndex: number;

    private onNotificationLayerValue: boolean = false;

    public get initialPositionY(): number {
        return this.initialPositionYValue;
    }

    public set initialPositionY(value: number) {
        this.initialPositionYValue = value;
    }

    public get shieldTarget(): DisplayObject {
        return this.shieldField;
    }

    public get coinsTarget(): DisplayObject {
        return this.coinsField;
    }

    public get onShopOpen(): Event<ButtonWithScaleAnimation, FederatedPointerEvent> {
        return this.coinsField.onPlusButtonClick;
    }

    public get onNotificationLayer(): boolean {
        return this.onNotificationLayerValue;
    }

    public constructor(
        commonResources: CommonResourcesPackage,
        userProfileResources: UserProfileResourcePackage,
        @inject("ISettingsController") settingsController: ISettingsController,
        @inject("IGuestModel") guestModel: IGuestModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        notificationLayerProvider: NotificationLayerProvider,
        @inject("ILogger") logger: ILogger
    ) {
        super(TopPanel.CHILD_SPACING);
        this.commonResources = commonResources;
        this.userProfileResources = userProfileResources;
        this.settingsController = settingsController;
        this.guestModel = guestModel;
        this.playerModel = playerModel;
        this.notificationLayer = notificationLayerProvider.notificationLayer;
        this.logger = logger;
        this.shieldsValue = playerModel.shields.value;
        this.onLayerChanged = new Event();
        this.animationTimeLine = new gsap.core.Timeline();
        this.build();
        this.layout();
        this.playerModel.propertyChanged.add(this.onPlayerModelPropertyChanged, this);
        this.once("added", this.onAddedToStage.bind(this));
    }

    private async onPlayerModelPropertyChanged(model: IPlayerModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "coins"):
                await this.updateCoins(model.coins.value)
                break;
            case nameof(model, "tokens"):
                const tokensValue = model.tokens.value ?? "0";
                await this.tokensField.updateTokens(tokensValue);
                break;
            case nameof(model, "shields"):
                await this.updateShields(model.shields.value)
                break;
        }
    }

    private build(): void {
        this.menuButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_top_panel_humburger_png);
        this.menuButton.width = 52;
        this.menuButton.height = 50;
        this.menuButton.onUp.add(this.userProfileButtonClickHande, this);
        this.addChild(this.menuButton);

        this.avatarField = new AvatarField(this.commonResources, this.userProfileResources, this.playerModel, this.guestModel);
        this.avatarField.onProfileButtonClick.add(this.userProfileButtonClickHande, this);
        this.addChild(this.avatarField);

        this.coinsField = new CoinsField(this.commonResources, this.playerModel.coins.value, this.settingsController, this.logger);
        this.addChild(this.coinsField);
        this.coinsFieldIndex = this.getChildIndex(this.coinsField);

        this.tokensField = new TokensField(this.commonResources, this.playerModel.tokens.value);
        this.addChild(this.tokensField);

        this.shieldField = new ShieldField(this.commonResources, this.shieldsValue);
        this.addChild(this.shieldField);
    }

    private layout(): void {
        this.menuButton.position.y = (this.coinsField.height - this.menuButton.height) * 0.5;
        this.shieldField.position.y = 5;
        this.alignmentStrategy();
    }

    private onAddedToStage(): void {
        this.originParent = this.parent;
        this.childIndexOnOriginalParent = this.originParent.getChildIndex(this);
    }

    private async updateShields(value: number): Promise<void> {
        if (this.shieldsValue > value) {
            await this.playLostShieldAnimation();
        }
        this.shieldField.shieldCount = value;
        this.shieldsValue = value;
    }

    private async playLostShieldAnimation(): Promise<void> {
        const particles = new ShieldPartsTexturePack(this.commonResources).textures;
        const animation = new BrokenShieldAnimation(
            this.shieldField,
            particles,
            [
                new Point(this.shieldField.width * 0.5, 100),
                new Point(this.shieldField.width * 0.5 - 60, 25),
                new Point(this.shieldField.width * 0.5 + 20, 15),
                new Point(this.shieldField.width * 0.5 - 70, 110),
                new Point(this.shieldField.width * 0.5 - 5, -10),
            ]
        );
        await animation.play();
    }

    private async updateCoins(value: number): Promise<void> {
        await this.coinsField.updateCoins(value);
    }

    public async userProfileButtonClickHande(): Promise<void> {
        this.interactiveChildren = false;
        await this.settingsController.showUserProfilePopup();
        this.interactiveChildren = true;
    }

    public bringCoinFieldToFront(): void {
        this.originParent.removeChild(this.coinsField);
        this.notificationLayer.addChild(this.coinsField);
        this.notificationLayer.setChildIndex(this.coinsField, 0);
        this.coinsField.position.set(this.position.x + this.coinsField.position.x, this.y);
    }

    public bringCoinFieldToOriginParent(): void {
        this.notificationLayer.removeChild(this.coinsField);
        this.addChildAt(this.coinsField, this.coinsFieldIndex);
        this.coinsField.position.y = 0;
        this.alignmentStrategy();
    }

    public bringToFront(ignoreEmit: boolean = false): void {
        this.originParent.removeChild(this);
        this.notificationLayer.addChild(this);
        this.notificationLayer.setChildIndex(this, 0);
        this.onNotificationLayerValue = true;
        if (!ignoreEmit) {
            this.onLayerChanged.emit();
        }
    }

    public bringToOriginParent(ignoreEmit: boolean = false): void {
        if (this.originParent == null) {
            this.logger.warn("Attemp of bring top panel to origin, without origin parent");
            return;
        }
        this.notificationLayer.removeChild(this);
        this.originParent.addChild(this);
        this.originParent.setChildIndex(this, this.childIndexOnOriginalParent);
        this.onNotificationLayerValue = false;
        if (!ignoreEmit) {
            this.onLayerChanged.emit();
        }
    }

    public async show(): Promise<void> {
        this.animationTimeLine.to(this.position, { y: this.initialPositionY, duration: 0.5, ease: "back.out(2)" });
        await this.animationTimeLine.play();
        this.animationTimeLine.clear();
    }

    public hide(): void {
        this.animationTimeLine.to(this.position, { y: -this.height, duration: 0.5, ease: "back.in(2)" });
        this.animationTimeLine.then(() => {
            this.animationTimeLine.clear();
        });
        this.animationTimeLine.play();
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.menuButton.onUp.remove(this.userProfileButtonClickHande, this);
        this.avatarField.onProfileButtonClick.remove(this.userProfileButtonClickHande, this);
        this.playerModel.propertyChanged.remove(this.onPlayerModelPropertyChanged, this);
        this.animationTimeLine.kill();
        super.destroy(options);
    }
}
